/**
 * The artist a roster seat last had open, remembered on the device (ART-358 / ART-361 / ART-360). On a phone
 * the Booking, Label and Management dashboards always show ONE artist: the one viewed last, else the roster's first artist by name. The
 * desktop rail records it too, so a phone picked up after a desktop session on the same browser lands where the
 * desktop left off.
 *
 * A cookie rather than storage because localStorage/sessionStorage are lint-banned (the `activeBand.ts` /
 * `theme.ts` precedent). Unlike the band selection it is a PERSISTENT cookie (a year): "last viewed" is meant to
 * outlive closing the app. It names the org too, so a remembered artist only applies inside the org it was seen
 * in. Not auth material: an id grants nothing, and every read is still gated server-side.
 */
const COOKIE_NAME = "cntrl_roster_artist";
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ONE_YEAR = 60 * 60 * 24 * 365;

function read(): { orgId: string; bandId: string } | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(/(?:^|;\s*)cntrl_roster_artist=([^;]*)/);
  const [orgId, bandId] = (match?.[1] ? decodeURIComponent(match[1]) : "").split(".");
  return orgId && bandId && UUID.test(orgId) && UUID.test(bandId) ? { orgId, bandId } : null;
}

/** Remember the artist opened under this org. */
export function rememberRosterArtist(orgId: string, bandId: string): void {
  if (typeof document === "undefined" || !UUID.test(orgId) || !UUID.test(bandId)) return;
  const current = read();
  if (current?.orgId === orgId && current.bandId === bandId) return;
  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(`${orgId}.${bandId}`)}; path=/; max-age=${ONE_YEAR}; samesite=lax`;
}

/**
 * Which artist the phone dashboard opens: the remembered one if it is still on this org's roster, else the first
 * artist alphabetically. Undefined when the roster is empty.
 */
export function resolveRosterArtistId(
  orgId: string | undefined,
  artists: ReadonlyArray<{ bandId?: string | null; name?: string | null }>,
): string | undefined {
  const ids = artists.filter((a) => a.bandId).map((a) => a.bandId as string);
  if (!orgId || ids.length === 0) return undefined;
  const remembered = read();
  if (remembered?.orgId === orgId && ids.includes(remembered.bandId)) return remembered.bandId;
  const first = [...artists]
    .filter((a) => a.bandId)
    .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "", undefined, { sensitivity: "base" }))[0];
  return first?.bandId ?? undefined;
}

/** The lenses whose phone dashboard is one artist at a time — all three since ART-360 brought Label over. */
export function isSingleArtistMobileLens(subview: string | null | undefined): boolean {
  return subview === "booking" || subview === "management" || subview === "label";
}
